import { FC } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, RefreshCcw } from 'lucide-react';

import { fetchImpactoStats } from '@/api/stats';
import { NbButton, NbCard, NbChip, NbPageHeader } from '@/components/Neobrut';
import { HeroMetric } from '@/components/Impacto/HeroMetric';
import { MonthlyBarChart } from '@/components/Impacto/MonthlyBarChart';
import { TopZonesBars } from '@/components/Impacto/TopZonesBars';

function formatPercent(rate: number | null): string {
  if (rate === null) return '—';
  return `${(rate * 100).toFixed(1)}%`;
}

function formatDays(days: number | null): string {
  if (days === null) return '—';
  return `${days}d`;
}

export const Impacto: FC = () => {
  const navigate = useNavigate();
  const {
    data,
    isLoading,
    isError,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['impacto-stats'],
    queryFn: fetchImpactoStats,
    staleTime: 60 * 1000,
  });

  return (
    <div className="mx-auto w-full max-w-2xl lg:max-w-3xl pb-10">
      <NbPageHeader
        slug="/ impacto"
        title={
          <>
            Los números
            <br />
            <span className="nb-highlight">detrás del reencuentro.</span>
          </>
        }
        subtitle="El trabajo de la red, en datos reales."
        fill="solved"
      />

      {isError && (
        <div
          className="fill-urgent mx-4 mt-3 flex items-center justify-between gap-2 px-3 py-2"
          style={{
            border: '2px solid var(--nb-ink)',
            borderRadius: 'var(--nb-radius)',
          }}
        >
          <div className="flex items-center gap-2 text-[13px] font-semibold">
            <AlertTriangle size={14} /> No pudimos cargar los números.
          </div>
          <NbButton
            fill="paper"
            size="sm"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <RefreshCcw size={12} /> Reintentar
          </NbButton>
        </div>
      )}

      {/* Hero grid */}
      <section className="grid grid-cols-2 gap-3 px-4 pt-4 sm:grid-cols-3">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="nb min-h-[110px] animate-pulse"
              style={{ background: '#e8e3d5' }}
            />
          ))
        ) : data ? (
          <>
            <HeroMetric
              fill="solved"
              value={data.totals.reunions_total}
              label="reencuentros"
            />
            <HeroMetric
              fill="paper"
              value={data.totals.publications_total}
              label="publicaciones"
            />
            <HeroMetric
              fill="paper"
              value={formatPercent(data.totals.success_rate)}
              label="tasa de éxito"
            />
            <HeroMetric
              fill="lost"
              value={data.totals.active_lost}
              label="buscados"
            />
            <HeroMetric
              fill="found"
              value={data.totals.active_found}
              label="esperando dueño"
            />
            <HeroMetric
              fill="paper"
              value={formatDays(data.totals.avg_days_to_reunion)}
              label="promedio búsqueda"
            />
          </>
        ) : null}
      </section>

      {/* Monthly chart */}
      <section className="px-4 pt-5">
        <NbCard className="p-4">
          <div
            className="text-[10px] font-bold uppercase tracking-[0.08em] opacity-60"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            / últimos 12 meses
          </div>
          <div className="font-display mt-1 text-[22px] leading-tight">
            Publicaciones por mes
          </div>
          <div className="mt-3">
            {isLoading || !data ? (
              <div
                className="animate-pulse"
                style={{ height: 180, background: '#e8e3d5' }}
              />
            ) : (
              <MonthlyBarChart data={data.monthly} />
            )}
          </div>
          <div className="mt-3 flex gap-2">
            <NbChip fill="lost">
              <span
                className="mr-1 inline-block h-2.5 w-2.5"
                style={{ background: 'var(--nb-lost)', border: '1.5px solid var(--nb-ink)' }}
              />
              Perdidos
            </NbChip>
            <NbChip fill="found">
              <span
                className="mr-1 inline-block h-2.5 w-2.5"
                style={{ background: 'var(--nb-found)', border: '1.5px solid var(--nb-ink)' }}
              />
              Encontrados
            </NbChip>
          </div>
        </NbCard>
      </section>

      {/* Top zones */}
      <section className="px-4 pt-5">
        <NbCard className="p-4">
          <div
            className="text-[10px] font-bold uppercase tracking-[0.08em] opacity-60"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            / top 5 ciudades
          </div>
          <div className="font-display mt-1 text-[22px] leading-tight">
            Dónde está activa la red
          </div>
          <div className="mt-4">
            {isLoading || !data ? (
              <div
                className="animate-pulse"
                style={{ height: 160, background: '#e8e3d5' }}
              />
            ) : (
              <TopZonesBars data={data.top_zones} />
            )}
          </div>
        </NbCard>
      </section>

      {/* Footer CTA */}
      <section className="px-4 pt-5">
        <NbCard className="fill-ink p-4 text-white">
          <div
            className="font-display text-[20px] leading-tight"
            style={{ color: 'var(--nb-paper)' }}
          >
            Si perdiste o encontraste
            <br />
            una mascota, publicala.
          </div>
          <div className="mt-3">
            <NbButton fill="lost" onClick={() => navigate('/publish')}>
              Publicar gratis →
            </NbButton>
          </div>
        </NbCard>
      </section>
    </div>
  );
};
